/**
 * P0 Clinic Dashboard Overview
 * Displays today's appointments, quick stats, and clinic overview
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Stack,
  Button,
  IconButton,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  LocalHospital as ClinicIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import dashboardApi from '../../api/dashboardApi';

const ClinicOverview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [stats, setStats] = useState(null);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      const [overviewData, statsData] = await Promise.all([
        dashboardApi.getOverview(),
        dashboardApi.getStats(),
      ]);
      setOverview(overviewData);
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching clinic overview:', err);
      setError(err.response?.data?.error || 'Failed to load clinic overview');
      toast.error('Failed to load clinic overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button size="small" onClick={fetchOverview}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!overview) {
    return (
      <Box p={3}>
        <Alert severity="info">No clinic data available</Alert>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      Scheduled: 'primary',
      Confirmed: 'info',
      Arrived: 'warning',
      'In Room': 'secondary',
      Completed: 'success',
      Canceled: 'error',
    };
    return colors[status] || 'default';
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Clinic Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {overview.clinic?.name || 'Clinic Dashboard'}
          </Typography>
        </Box>
        <IconButton onClick={fetchOverview} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CalendarIcon color="primary" />
                <Box>
                  <Typography variant="h4">{overview.stats?.today_count || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Today's Appointments
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <TimeIcon color="info" />
                <Box>
                  <Typography variant="h4">{overview.stats?.upcoming_today || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upcoming Today
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CheckCircleIcon color="success" />
                <Box>
                  <Typography variant="h4">{overview.stats?.completed_today || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Today
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CancelIcon color="error" />
                <Box>
                  <Typography variant="h4">{overview.stats?.no_shows_today || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    No Shows Today
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Today's Appointments by Doctor */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper>
            <Box p={2}>
              <Typography variant="h6" gutterBottom>
                Today's Appointments
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {overview.today_appointments && overview.today_appointments.length > 0 ? (
                overview.today_appointments.map((doctorGroup) => (
                  <Box key={doctorGroup.doctor.id} mb={3}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      {doctorGroup.doctor.name}
                    </Typography>
                    <List>
                      {doctorGroup.appointments.map((appointment) => (
                        <ListItem
                          key={appointment.id}
                          sx={{
                            borderLeft: `3px solid`,
                            borderColor: `${getStatusColor(appointment.status)}.main`,
                            mb: 1,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar>
                              <PersonIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={appointment.patient_name}
                            secondary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption">
                                  {appointment.scheduled_time}
                                </Typography>
                                <Chip
                                  label={appointment.status}
                                  size="small"
                                  color={getStatusColor(appointment.status)}
                                />
                              </Stack>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))
              ) : (
                <Alert severity="info">No appointments scheduled for today</Alert>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar Stats */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            <Paper>
              <Box p={2}>
                <Typography variant="h6" gutterBottom>
                  Quick Stats
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Upcoming This Week
                    </Typography>
                    <Typography variant="h5">
                      {overview.stats?.upcoming_this_week || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Doctors Active
                    </Typography>
                    <Typography variant="h5">
                      {overview.doctors_count || 0}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            {stats && (
              <Paper>
                <Box p={2}>
                  <Typography variant="h6" gutterBottom>
                    Monthly Stats
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        This Month
                      </Typography>
                      <Typography variant="h6">
                        {stats.this_month?.total || 0} appointments
                      </Typography>
                      <Typography variant="caption" color="success.main">
                        {stats.this_month?.completed || 0} completed
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Last Month
                      </Typography>
                      <Typography variant="h6">
                        {stats.last_month?.total || 0} appointments
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClinicOverview;

